import { Box, Flex, Link, Select, Skeleton, Text, useDisclosure } from "@chakra-ui/react";
import * as React from "react";

import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

// Custom components
import Card from "components/card/Card";
import ClientCombobox from "components/list/ClientCombobox";
import ListTable, { LIST_MUTED_COLOR, ListHeader, ListRow } from "components/list/ListTable";
import { usePagination } from "components/list/TablePagination";
import { useClient } from "hooks/useClient";
import { useForfait } from "hooks/useForfait";
import { useListState } from "hooks/useListState";
import ModalForm from "components/modal/modalForm";
import ListMenuSite from "components/menu/ListMenuSite";
import { useHistory, useLocation } from "react-router-dom";
import { CMS_LABELS, SAUVEGARDE_LABELS, sauvegardeHorsAkeeba } from "utils";

// `forfaitActif` : undefined tant que les forfaits ne sont pas lus
type TableSiteType = SiteType & { client: string; forfaitActif?: string };

const avecCasse = (texte: string) => texte.toLocaleLowerCase("fr");

const columnHelper = createColumnHelper<TableSiteType>();

// Type des forfaits actifs d'un site : « Horaire », « Annuel », les deux, ou vide
function forfaitActifDuSite(idSite: string, forfaits: ForfaitType[] | null) {
  if (!forfaits) {
    return undefined;
  }
  const actifs = forfaits.filter((forfait) => forfait.statut === true && forfait.idsites?.includes(idSite));
  const types = [actifs.some((forfait) => forfait.type === 1) && "Horaire", actifs.some((forfait) => forfait.type === 2) && "Annuel"];
  return types.filter(Boolean).join(" + ");
}

export default function ColumnTable(props: { tableData: SiteType[] }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { tableData } = props;
  const [sorting, setSorting] = useListState<SortingState>("sites:tri", [{ id: "client", desc: false }]);
  const [selectedSite, setSelectedSite] = React.useState<SiteType[] | null>(null);
  const [selectedClient, setSelectedClient] = useListState("sites:client", "");
  const [selectedCms, setSelectedCms] = useListState("sites:cms", "");
  const [selectedEtat, setSelectedEtat] = useListState("sites:etat", "");
  const [allData, setAllData] = React.useState<SiteType[]>(() => [...tableData]);
  const [clients, setClients] = React.useState<ClientType[]>([]);
  const { getClients } = useClient();
  const { fetch: fetchForfaits } = useForfait();
  const [forfaits, setForfaits] = React.useState<ForfaitType[] | null>(null);

  // Colonnes créées une seule fois : de nouvelles fonctions `cell` à chaque rendu feraient remonter toutes les cellules
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("client", {
        id: "client",
        header: "Client",
        meta: { width: { base: "220px", "2xl": "24%" } },
        sortingFn: (a, b) => avecCasse(a.original.client).localeCompare(avecCasse(b.original.client), "fr"),
        cell: (info) => <Text fontWeight="700">{info.getValue()}</Text>,
      }),
      columnHelper.accessor("nom", {
        id: "nom",
        header: "Site",
        // Sans tenir compte de la casse : un nom choisi se classe parmi les domaines
        sortingFn: (a, b) => a.original.nom.localeCompare(b.original.nom, "fr", { sensitivity: "base" }),
        cell: (info) => (
          <>
            <Link href={info.row.original.url} isExternal>
              {info.getValue()}
            </Link>
            {info.getValue() !== info.row.original.domaine && (
              <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
                {info.row.original.domaine}
              </Text>
            )}
          </>
        ),
      }),
      columnHelper.accessor("cms", {
        id: "cms",
        header: "CMS",
        meta: { width: "130px" },
        // Une sauvegarde hors Akeeba se lit sous le CMS : c'est l'exception
        cell: (info) => (
          <>
            {CMS_LABELS[info.getValue()] ?? info.getValue()}
            {sauvegardeHorsAkeeba(info.row.original) && (
              <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
                Sauvegarde : {SAUVEGARDE_LABELS[info.row.original.sauvegarde].toLocaleLowerCase("fr")}
              </Text>
            )}
          </>
        ),
      }),
      columnHelper.accessor("forfaitActif", {
        id: "forfait_actif",
        header: "Forfait actif",
        meta: { width: "150px" },
        // Lu pour toute la liste : le temps restant se suit dans la page Maintenance WordPress
        enableSorting: false,
        cell: (info) =>
          info.getValue() === undefined ? (
            <Skeleton h="8px" w="80px" borderRadius="full" />
          ) : (
            <Text as="span" color={info.getValue() ? undefined : LIST_MUTED_COLOR}>
              {info.getValue() || "Aucun"}
            </Text>
          ),
      }),
      columnHelper.accessor("actif", {
        id: "actif",
        header: "État",
        meta: { width: "90px" },
        // Actif : la pastille seule. Seul l'archivage, qui est l'exception, s'écrit en toutes lettres
        cell: (info) => (
          <Flex align="center" gap="6px">
            <Box
              role="img"
              aria-label={info.getValue() ? "Actif" : undefined}
              title={info.getValue() ? "Actif" : undefined}
              w="8px"
              h="8px"
              borderRadius="50%"
              flexShrink={0}
              bg={info.getValue() ? "green.500" : "secondaryGray.500"}
            />
            {!info.getValue() && "Archivé"}
          </Flex>
        ),
      }),
      columnHelper.display({
        id: "actions",
        meta: { width: "67px", align: "right" },
        cell: (info) => (
          <ListMenuSite
            siterow={[info.row.original]}
            openmodal={onOpen}
            setselectedsite={setSelectedSite}
            setData={setAllData}
          />
        ),
      }),
    ],
    [onOpen]
  );

  // Le nom du client est lu une seule fois, pour toute la liste
  const data = React.useMemo(() => {
    const names = new Map(clients.map((client) => [client.id, client.raison_sociale]));
    return allData
      .filter(
        (site) =>
          (!selectedClient || site.idclient === selectedClient) &&
          (!selectedCms || site.cms === selectedCms) &&
          (!selectedEtat || site.actif === (selectedEtat === "actif"))
      )
      .map((site) => ({ ...site, client: names.get(site.idclient) ?? "", forfaitActif: forfaitActifDuSite(site.id, forfaits) }));
  }, [allData, clients, forfaits, selectedClient, selectedCms, selectedEtat]);

  const [pagination, setPagination] = usePagination(data.length);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      table.setPageIndex(0);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    // Garde la page après une modification ; le tri, les filtres et les ajouts reviennent à la première
    autoResetPageIndex: false,
  });

  React.useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsData = await getClients();
        setClients(clientsData);
        // Client gardé en filtre puis supprimé : le filtre est abandonné
        setSelectedClient((current) =>
          current && !clientsData.some((client) => client.id === current) ? "" : current
        );
      } catch (error) {
        console.error("Erreur lors de la récupération des clients :", error);
      }
    };
    fetchClients();
  }, [getClients, setSelectedClient]);

  React.useEffect(() => {
    let cancelled = false;
    fetchForfaits()
      .then((rows) => {
        if (!cancelled) {
          setForfaits(rows);
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [fetchForfaits]);

  // Lien « Voir les sites » depuis un client : il devient le filtre courant
  const location = useLocation();
  const history = useHistory();
  const idClient = new URLSearchParams(location.search).get("idclient");
  React.useEffect(() => {
    if (idClient) {
      setSelectedClient(idClient);
      setSelectedCms("");
      setSelectedEtat("");
      history.replace(location.pathname);
    }
  }, [idClient, setSelectedClient, setSelectedCms, setSelectedEtat, history, location.pathname]);

  function changeFilter(setter: (value: string) => void) {
    return (event: React.ChangeEvent<HTMLSelectElement>) => {
      setter(event.target.value);
      table.setPageIndex(0);
    };
  }
  // Un ajout revient à la première page
  function handleFormData(newData: SiteType[]) {
    setAllData(newData);
    if (!selectedSite) {
      table.setPageIndex(0);
    }
  }
  function handleAdd() {
    setSelectedSite(null);
    onOpen();
  }
  return (
    <Card flexDirection="column" w="100%" px="0px" pt="16px" pb="8px">
      <ListHeader title={`Liste des sites (${data.length})`} addLabel="Ajouter un site" onAdd={handleAdd}>
        <ClientCombobox
          clients={clients}
          value={selectedClient}
          onChange={(id) => {
            setSelectedClient(id);
            table.setPageIndex(0);
          }}
        />
        <Select
          aria-label="Filtrer par CMS"
          placeholder="Tous les CMS"
          size="sm"
          width="150px"
          borderRadius="10px"
          value={selectedCms}
          onChange={changeFilter(setSelectedCms)}
        >
          {Object.entries(CMS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrer par état"
          placeholder="Tous les états"
          size="sm"
          width="150px"
          borderRadius="10px"
          value={selectedEtat}
          onChange={changeFilter(setSelectedEtat)}
        >
          <option value="actif">Actifs</option>
          <option value="archive">Archivés</option>
        </Select>
      </ListHeader>
      <ListTable
        table={table}
        renderRow={(row) => <ListRow key={row.id} row={row} muted={!row.original.actif} />}
      />
      <ModalForm<SiteType>
        isOpen={isOpen}
        onClose={onClose}
        newData={handleFormData}
        updateData={selectedSite}
        form="site"
        defaultIdClient={selectedClient}
      />
    </Card>
  );
}
